import React, { useState } from "react";
import { Button } from "@design-system/Button/Button";
import { Field } from "@design-system/Field/Field";
import {
    TagsInput,
    type TagsInputProps,
    type TagsInputSize,
} from "@design-system/TagsInput/TagsInput";
import { VStack } from "@design-system/VStack/VStack";
import {
    useAnatomyMode,
    type AnatomyForcedState,
} from "./componentAnatomyMode";
import {
    BooleanControl,
    SelectControl,
    TextControl,
} from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./SpecializedInputPlayground.module.scss";
import { TagsInputMatrix } from "./TagsInputPlaygroundMatrix";

type SeparatorPreset = "default" | "Enter only";

const SIZES: readonly TagsInputSize[] = ["lg", "xl"];
const SEPARATOR_PRESETS: readonly SeparatorPreset[] = ["default", "Enter only"];
const separatorKeysByPreset: Record<
    SeparatorPreset,
    readonly string[] | undefined
> = {
    default: undefined,
    "Enter only": ["Enter"],
};
const initialValues = ["Payroll", "Invoices"];

const toLowerCase = (value: string) => value.toLowerCase();
const isEmail = (value: string) => value.includes("@");

/** TagsInput has no pressed state, so that one state drops on the way through. */
const toTagsInputForcedState = (
    state: AnatomyForcedState | undefined,
): TagsInputProps["data-forced-state"] => {
    switch (state) {
        case "hover":
        case "focus":
            return state;
        default:
            return undefined;
    }
};

/** Sandbox for the controlled TagsInput: every prop is a live control. */
export const TagsInputPlayground = () => {
    const [values, setValues] = useState(initialValues);
    const [invalidValues, setInvalidValues] = useState<string[]>([]);
    const [placeholder, setPlaceholder] = useState("Type or paste values");
    const [size, setSize] = useState<TagsInputSize>("xl");
    const [separatorPreset, setSeparatorPreset] =
        useState<SeparatorPreset>("default");
    const [shouldNormalizeCase, setShouldNormalizeCase] = useState(false);
    const [shouldValidateEmail, setShouldValidateEmail] = useState(false);
    const [isInvalid, setIsInvalid] = useState(false);

    const variantId = isInvalid ? "invalid" : "default";
    const anatomy = useAnatomyMode({
        component: "tagsInput",
        variantId,
        sizeId: size,
        isDisabled: false,
        tagsInputContent: { values, placeholder },
    });
    const forcedState = toTagsInputForcedState(anatomy.forcedState);

    return (
        <SandboxLayout
            controls={
                <>
                    {anatomy.control}
                    <section>
                        <h2>Content</h2>
                        <VStack gap={12}>
                            <TextControl
                                label="Placeholder"
                                value={placeholder}
                                onChange={setPlaceholder}
                            />
                            <SelectControl
                                label="Size"
                                options={SIZES}
                                value={size}
                                onChange={setSize}
                            />
                            <SelectControl
                                label="Separator keys"
                                options={SEPARATOR_PRESETS}
                                value={separatorPreset}
                                onChange={setSeparatorPreset}
                            />
                            <BooleanControl
                                label="Normalize to lowercase"
                                checked={shouldNormalizeCase}
                                onChange={setShouldNormalizeCase}
                            />
                            <BooleanControl
                                label="Validate as email"
                                checked={shouldValidateEmail}
                                onChange={setShouldValidateEmail}
                            />
                            <BooleanControl
                                label="Invalid"
                                checked={isInvalid}
                                onChange={setIsInvalid}
                            />
                        </VStack>
                    </section>
                    <section className={styles.fields}>
                        <h2>Try it</h2>
                        <p className={styles.hint}>
                            Add values with the separator keys. Paste several
                            values separated by spaces, commas, or semicolons.
                            Backspace in an empty input removes the last token.
                            Text that fails validation stays in the input.
                        </p>
                        <Button
                            type="button"
                            variant="primary"
                            size="md"
                            onClick={() => {
                                setValues(initialValues);
                                setInvalidValues([]);
                            }}
                        >
                            Reset tags
                        </Button>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                width={anatomy.stageWidth}
                summary={
                    anatomy.isActive
                        ? `x-ray · ${variantId} · ${size}`
                        : `${values.length} tokens`
                }
                belowStage={<TagsInputMatrix />}
                output={JSON.stringify(
                    {
                        props: {
                            size,
                            placeholder,
                            separatorKeys:
                                separatorKeysByPreset[separatorPreset] ??
                                "default",
                            normalizeValue: shouldNormalizeCase
                                ? "lowercase"
                                : null,
                            isValidValue: shouldValidateEmail ? "email" : null,
                            invalid: isInvalid,
                            forcedState: forcedState ?? null,
                        },
                        value: values,
                        lastInvalidValues: invalidValues,
                    },
                    null,
                    2,
                )}
            >
                {anatomy.view ?? (
                    <Field
                        label="Emails"
                        error={
                            isInvalid
                                ? "Enter at least one valid email"
                                : undefined
                        }
                    >
                        <TagsInput
                            value={values}
                            onValueChange={setValues}
                            size={size}
                            placeholder={placeholder}
                            separatorKeys={
                                separatorKeysByPreset[separatorPreset]
                            }
                            normalizeValue={
                                shouldNormalizeCase ? toLowerCase : undefined
                            }
                            isValidValue={
                                shouldValidateEmail ? isEmail : undefined
                            }
                            onInvalidValues={setInvalidValues}
                            data-forced-state={forcedState}
                        />
                    </Field>
                )}
            </ShowcasePreview>
        </SandboxLayout>
    );
};
